import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { opsApi } from './lib/ops-api';
import { usePolling } from './hooks/usePolling';
import LoginScreen from './screens/LoginScreen';
import Layout from './components/Layout';
import OverviewPage from './pages/OverviewPage';
import TeamDetailPage from './pages/TeamDetailPage';
import PlatformHealthPage from './pages/PlatformHealthPage';
import type { OverviewResponse } from './lib/types';

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  // Fetch overview once to determine platform admin status
  const { data: overview } = usePolling<OverviewResponse>(
    () => opsApi.getOverview(),
    300_000, // Refresh role check every 5 min
    isAuthenticated,
  );

  const isPlatformAdmin = overview?.is_platform_admin ?? false;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-surface-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout isPlatformAdmin={isPlatformAdmin} />}>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/teams/:teamId" element={<TeamDetailPage />} />
          {isPlatformAdmin && (
            <Route path="/platform" element={<PlatformHealthPage />} />
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
